import type { ClientUserPermissions } from "@/lib/auth/client-permissions";
import { canShowClientNavItem, clientDashboardNavHasData } from "@/components/sidebar/client-nav-visibility";
import {
  ADMIN_LINKS,
  AGENCY_LINKS,
  AUDIT_LINKS,
  RESEARCH_LINKS,
  INTEGRATION_LINKS,
  PERSONAL_LINKS,
  STANDARD_VIEWS,
  WORKSPACE_LINKS,
  type AgencyDestination,
  type NavDestination,
} from "@/lib/nav/registry";
import { canReadDocTopic, type DocTopic } from "@/lib/docs/registry";
import { searchDocumentation } from "@/lib/docs/search";
import { compareResults, matchLabel } from "./match";
import type { SearchResult } from "./types";

/**
 * Everything global search knows how to find, as pure functions over inputs the
 * caller already has. No fetching here: the company list and the client's
 * enabled-provider payload are both in client state by the time search opens.
 *
 * The gating is the point. A destination search surfaces but the sidebar hides
 * is a permissions leak in a new coat, so these reuse canShowClientNavItem and
 * clientDashboardNavHasData rather than re-deriving visibility.
 */

export interface ClientSearchContext {
  clientId: string;
  clientName: string;
  providers: string[];
  hiddenViews: string[];
  permissions: ClientUserPermissions | null;
  customDashboards: { id: string; name: string }[];
  socialEnabled?: boolean;
  sitemapsEnabled?: boolean;
}

export interface SearchContext {
  role: string;
  demoMode: boolean;
  /** Null outside a company. */
  client: ClientSearchContext | null;
}

const STAFF_ROLES = new Set(["developer", "admin", "manager"]);

export function isStaff(role: string): boolean {
  return STAFF_ROLES.has(role);
}

/** Company page hrefs keep the range/compare the user is currently looking at. */
function clientHref(clientId: string, path: string, params: string): string {
  const [pathname, existing = ""] = path.split("?", 2);
  const merged = new URLSearchParams(existing);
  new URLSearchParams(params).forEach((value, key) => {
    if (!merged.has(key)) merged.set(key, value);
  });
  const query = merged.toString();
  const base = `/clients/${clientId}/${pathname}`;
  return query ? `${base}?${query}` : base;
}

const CLIENT_SUBDESTINATIONS: Array<{
  key: string;
  parent: string;
  label: string;
  path: string;
  synonyms?: readonly string[];
}> = [
  { key: "ga4-channels", parent: "ga4", label: "GA4 Channels", path: "integrations/ga4?tab=channels", synonyms: ["traffic channels", "channel breakdown"] },
  { key: "ga4-pages", parent: "ga4", label: "GA4 Pages", path: "integrations/ga4?tab=pages", synonyms: ["landing pages", "page views"] },
  { key: "ga4-sources", parent: "ga4", label: "GA4 Sources", path: "integrations/ga4?tab=sources", synonyms: ["source medium", "referrers"] },
  { key: "ga4-events", parent: "ga4", label: "GA4 Events", path: "integrations/ga4?tab=events", synonyms: ["conversions", "key events"] },
  { key: "ga4-trends", parent: "ga4", label: "GA4 Trends", path: "integrations/ga4?tab=trends", synonyms: ["analytics history"] },
  { key: "ga4-paths", parent: "ga4", label: "Path Explorer", path: "integrations/ga4?tab=paths", synonyms: ["user paths", "user flow", "journey", "navigation paths", "sankey", "page transitions"] },
  { key: "ga4-explorer", parent: "ga4", label: "GA4 Explorer", path: "integrations/ga4?tab=explorer", synonyms: ["filter analytics", "dimensions"] },
  { key: "gsc-queries", parent: "gsc", label: "Search Queries", path: "integrations/gsc?tab=queries", synonyms: ["gsc keywords", "search terms"] },
  { key: "gsc-pages", parent: "gsc", label: "Search Console Pages", path: "integrations/gsc?tab=pages", synonyms: ["organic landing pages"] },
  { key: "gsc-opportunities", parent: "gsc", label: "Search Opportunities", path: "integrations/gsc?tab=opportunities", synonyms: ["striking distance", "seo opportunities"] },
  { key: "gsc-explorer", parent: "gsc", label: "Search Console Explorer", path: "integrations/gsc?tab=explorer", synonyms: ["filter gsc", "query explorer"] },
  { key: "google-ads-campaigns", parent: "google-ads", label: "Google Ads Campaigns", path: "integrations/google-ads?tab=campaigns" },
  { key: "google-ads-groups", parent: "google-ads", label: "Google Ads Ad Groups", path: "integrations/google-ads?tab=ad-groups", synonyms: ["adgroups"] },
  { key: "google-ads-keywords", parent: "google-ads", label: "Google Ads Keywords", path: "integrations/google-ads?tab=keywords", synonyms: ["paid search terms"] },
  { key: "meta-ads-campaigns", parent: "meta-ads", label: "Meta Ads Campaigns", path: "integrations/meta-ads?tab=campaigns", synonyms: ["facebook campaigns"] },
  { key: "meta-ads-ads", parent: "meta-ads", label: "Meta Ads Creative", path: "integrations/meta-ads?tab=ads", synonyms: ["facebook ads"] },
  { key: "stackadapt-campaigns", parent: "stackadapt", label: "StackAdapt Campaigns", path: "integrations/stackadapt?tab=campaigns" },
  { key: "stackadapt-creatives", parent: "stackadapt", label: "StackAdapt Creatives", path: "integrations/stackadapt?tab=creatives" },
  { key: "callrail-calls", parent: "callrail", label: "CallRail Calls", path: "integrations/callrail?tab=calls", synonyms: ["call log", "phone calls"] },
  { key: "callrail-forms", parent: "callrail", label: "CallRail Forms", path: "integrations/callrail?tab=forms", synonyms: ["form leads"] },
  { key: "gbp-reviews", parent: "gbp", label: "Google Business Reviews", path: "integrations/gbp?tab=reviews", synonyms: ["gbp reviews"] },
  { key: "wordpress-maintenance", parent: "wordpress", label: "WordPress Maintenance", path: "integrations/wordpress?tab=maintenance", synonyms: ["updates", "plugins", "themes"] },
  { key: "wordpress-content", parent: "wordpress", label: "WordPress Content", path: "integrations/wordpress?tab=content", synonyms: ["posts", "pages"] },
  { key: "wordpress-activity", parent: "wordpress", label: "WordPress Activity", path: "integrations/wordpress?tab=activity", synonyms: ["recent changes"] },
  { key: "pagespeed-opportunities", parent: "pagespeed", label: "Performance Opportunities", path: "integrations/pagespeed?tab=opportunities", synonyms: ["lighthouse issues", "speed fixes"] },
  { key: "pagespeed-pages", parent: "pagespeed", label: "Performance Audit Pages", path: "integrations/pagespeed?tab=pages" },
  { key: "pagespeed-history", parent: "pagespeed", label: "Performance Audit History", path: "integrations/pagespeed?tab=history" },
  { key: "site-audit-checks", parent: "site-audit", label: "Security Checks", path: "integrations/audit?tab=checks", synonyms: ["technical checks", "headers"] },
  { key: "site-audit-history", parent: "site-audit", label: "Security Audit History", path: "integrations/audit?tab=history" },
  { key: "seo-crawl-issues", parent: "seo-crawl", label: "SEO Audit Issues", path: "integrations/seo-crawl?tab=issues", synonyms: ["crawl errors"] },
  { key: "seo-crawl-links", parent: "seo-crawl", label: "SEO Audit Links", path: "integrations/seo-crawl?tab=links", synonyms: ["internal links"] },
  { key: "seo-crawl-pages", parent: "seo-crawl", label: "SEO Audit Pages", path: "integrations/seo-crawl?tab=pages" },
  { key: "seo-crawl-history", parent: "seo-crawl", label: "SEO Audit History", path: "integrations/seo-crawl?tab=history" },
  { key: "accessibility-wcag", parent: "accessibility", label: "Accessibility Issues", path: "integrations/accessibility?tab=wcag", synonyms: ["wcag violations", "a11y issues"] },
  { key: "accessibility-manual", parent: "accessibility", label: "Manual Accessibility Checks", path: "integrations/accessibility?tab=manual" },
  { key: "accessibility-pages", parent: "accessibility", label: "Accessibility Pages", path: "integrations/accessibility?tab=pages" },
  { key: "accessibility-history", parent: "accessibility", label: "Accessibility History", path: "integrations/accessibility?tab=history" },
  { key: "keyword-landscape", parent: "keyword-tracker", label: "Keyword Landscape", path: "integrations/keyword-tracker?tab=landscape", synonyms: ["rank distribution"] },
  { key: "keyword-trends", parent: "keyword-tracker", label: "Keyword Trends", path: "integrations/keyword-tracker?tab=trends", synonyms: ["ranking history"] },
  { key: "keyword-pages", parent: "keyword-tracker", label: "Ranking Pages", path: "integrations/keyword-tracker?tab=pages", synonyms: ["keyword pages"] },
  { key: "keyword-cannibalization", parent: "keyword-tracker", label: "Keyword Cannibalization", path: "integrations/keyword-tracker?tab=cannibalization", synonyms: ["competing pages"] },
  { key: "seo-competitors", parent: "seo-research", label: "SEO Competitors", path: "integrations/seo-research?tab=competitors", synonyms: ["organic competitors"] },
  { key: "seo-backlinks", parent: "seo-research", label: "Backlinks", path: "integrations/seo-research?tab=backlinks", synonyms: ["referring domains"] },
  { key: "seo-opportunities", parent: "seo-research", label: "SEO Opportunities", path: "integrations/seo-research?tab=opportunities" },
  { key: "seo-keyword-gap", parent: "seo-research", label: "Keyword Gap", path: "integrations/seo-research?tab=keyword-gap", synonyms: ["content gap"] },
  { key: "seo-pages", parent: "seo-research", label: "SEO Research Pages", path: "integrations/seo-research?tab=pages", synonyms: ["battleground pages"] },
  { key: "seo-keyword-research", parent: "seo-research", label: "Keyword Research", path: "integrations/seo-research?tab=keyword-research", synonyms: ["keyword ideas", "search volume"] },
  { key: "seo-public-opinion", parent: "seo-research", label: "Public Opinion", path: "integrations/seo-research?tab=public-opinion", synonyms: ["sentiment", "reputation"] },
  { key: "aeo-prompts", parent: "aeo-research", label: "AEO Prompts", path: "integrations/aeo-research?tab=prompts", synonyms: ["tracked questions"] },
  { key: "aeo-citations", parent: "aeo-research", label: "AEO Citations", path: "integrations/aeo-research?tab=citations", synonyms: ["ai sources"] },
  { key: "aeo-recommendations", parent: "aeo-research", label: "AEO Recommendations", path: "integrations/aeo-research?tab=recommendations" },
  { key: "aeo-rivals", parent: "aeo-research", label: "AEO Rivals", path: "integrations/aeo-research?tab=rivals", synonyms: ["ai competitors"] },
  { key: "aeo-footprint", parent: "aeo-research", label: "AI Visibility Footprint", path: "integrations/aeo-research?tab=footprint", synonyms: ["ai mentions"] },
];

function visibleForProviders(
  destination: NavDestination,
  providers: string[],
  staff: boolean,
): boolean {
  if (destination.provider && !providers.includes(destination.provider)) return false;
  if (destination.staffOnly && !staff) return false;
  return true;
}

/**
 * Company destinations for the company currently open. Applies the sidebar's
 * two gates: provider/staff presence, then the client-user grant plus the
 * has-content rule.
 */
export function searchClientPages(
  query: string,
  ctx: SearchContext,
  queryParams = "",
): SearchResult[] {
  const { client } = ctx;
  if (!client) return [];

  const staff = isStaff(ctx.role);
  const hidden = new Set(client.hiddenViews);
  const providerSet = new Set(client.providers);
  const results: SearchResult[] = [];

  const canShow = (permission: NavDestination["permission"], hasContent = true) =>
    !permission ||
    canShowClientNavItem(permission, {
      demoMode: ctx.demoMode,
      hasContent,
      isStaff: staff,
      permissions: client.permissions,
    });

  const push = (d: NavDestination, hasData = true) => {
    const hit = matchLabel(d.label, query, d.synonyms);
    if (!hit) return;
    results.push({
      id: `page:${d.key}`,
      kind: "page",
      category: "navigate",
      title: d.label,
      subtitle: client.clientName,
      href: clientHref(client.clientId, d.path, queryParams),
      iconKey: d.iconKey,
      matches: hit.matches,
      // A destination with no data yet still exists for staff, but it should
      // never outrank one that does.
      score: hit.score - (hasData ? 0 : 25),
    });
  };

  for (const view of STANDARD_VIEWS) {
    if (hidden.has(view.key)) continue;
    const hasData = ctx.demoMode || clientDashboardNavHasData(view.key, providerSet);
    if (!canShow(view.permission, hasData)) continue;
    push(view, hasData);
  }

  for (const link of [...INTEGRATION_LINKS, ...AUDIT_LINKS, ...RESEARCH_LINKS]) {
    if (!visibleForProviders(link, client.providers, staff)) continue;
    push(link);
  }

  const destinationByKey = new Map(
    [...INTEGRATION_LINKS, ...AUDIT_LINKS, ...RESEARCH_LINKS].map((destination) => [destination.key, destination]),
  );
  for (const subpage of CLIENT_SUBDESTINATIONS) {
    const parent = destinationByKey.get(subpage.parent);
    if (!parent || !visibleForProviders(parent, client.providers, staff)) continue;
    const hit = matchLabel(subpage.label, query, subpage.synonyms);
    if (!hit) continue;
    results.push({
      id: `page:sub:${subpage.key}`,
      kind: "page",
      category: "navigate",
      title: subpage.label,
      subtitle: `${parent.label} · ${client.clientName}`,
      href: clientHref(client.clientId, subpage.path, queryParams),
      iconKey: parent.iconKey,
      matches: hit.matches,
      score: hit.score,
    });
  }

  for (const link of WORKSPACE_LINKS) {
    if (link.staffOnly && !staff) continue;
    if (hidden.has(link.key)) continue;
    if (link.permission === "pulse.view" && hidden.has("pulse")) continue;
    if (!canShow(link.permission)) continue;
    push(link);
  }

  for (const dashboard of client.customDashboards) {
    const hit = matchLabel(dashboard.name, query);
    if (!hit) continue;
    results.push({
      id: `page:custom:${dashboard.id}`,
      kind: "page",
      category: "navigate",
      title: dashboard.name,
      subtitle: `${client.clientName} · Custom dashboard`,
      href: clientHref(client.clientId, `custom/${dashboard.id}`, queryParams),
      iconKey: "grid",
      matches: hit.matches,
      score: hit.score,
    });
  }

  results.push(...searchPublishingPages(query, ctx, queryParams));
  return results.sort(compareResults);
}

/**
 * Publishing lives behind two independent per-company toggles, and its surfaces
 * are built inline by the sidebar rather than from a table, so they are
 * enumerated here against the same conditions.
 */
function searchPublishingPages(
  query: string,
  ctx: SearchContext,
  queryParams: string,
): SearchResult[] {
  const { client } = ctx;
  if (!client) return [];

  const staff = isStaff(ctx.role);
  const hidden = new Set(client.hiddenViews);
  const canShow = (permission: NavDestination["permission"]) =>
    !permission ||
    canShowClientNavItem(permission, {
      demoMode: ctx.demoMode,
      isStaff: staff,
      permissions: client.permissions,
    });

  const social = Boolean(client.socialEnabled) && canShow("social.view") && !hidden.has("social_publishing");
  const sitemaps = Boolean(client.sitemapsEnabled) && canShow("sitemaps.view") && !hidden.has("sitemap_planner");
  const media =
    (Boolean(client.socialEnabled) || Boolean(client.sitemapsEnabled)) &&
    canShow("media.view") &&
    !hidden.has("media_library");

  const entries: { key: string; label: string; path: string; iconKey: string; on: boolean; synonyms: string[] }[] = [
    { key: "publishing/calendar", label: "Calendar", path: "publishing/calendar", iconKey: "calendar", on: social || sitemaps, synonyms: ["schedule", "publishing"] },
    { key: "publishing/content", label: "Content", path: "publishing/content", iconKey: "megaphone", on: social || sitemaps, synonyms: ["posts", "pages", "publishing"] },
    { key: "publishing/approvals", label: "Approvals", path: "publishing/approvals", iconKey: "checkCircle", on: social || sitemaps, synonyms: ["review", "pending", "publishing"] },
    { key: "publishing/sitemaps", label: "Sitemaps", path: "publishing/sitemaps", iconKey: "shareNodes", on: sitemaps, synonyms: ["content planner", "pages"] },
    { key: "publishing/media", label: "Media", path: "publishing/media", iconKey: "photo", on: media, synonyms: ["images", "library", "assets"] },
  ];

  return entries.flatMap((entry) => {
    if (!entry.on) return [];
    const hit = matchLabel(entry.label, query, entry.synonyms);
    if (!hit) return [];
    return [{
      id: `page:${entry.key}`,
      kind: "page" as const,
      category: "navigate" as const,
      title: entry.label,
      subtitle: `${client.clientName} · Publishing`,
      href: clientHref(client.clientId, entry.path, queryParams),
      iconKey: entry.iconKey,
      matches: hit.matches,
      score: hit.score,
    }];
  });
}

/** Agency, admin, and personal destinations, gated on role. */
export function searchAgencyPages(query: string, ctx: SearchContext): SearchResult[] {
  const staff = isStaff(ctx.role);
  // My Account is the one destination a client user has; everything else here
  // is staff, matching what the rail and the top bar show them.
  const allowed = (link: AgencyDestination) =>
    (staff || link.key === "account") && (!link.roles || link.roles.includes(ctx.role));

  return [...AGENCY_LINKS, ...ADMIN_LINKS, ...PERSONAL_LINKS]
    .flatMap((link) => {
      if (!allowed(link)) return [];
      const hit = matchLabel(link.label, query, link.synonyms);
      if (!hit) return [];
      return [{
        id: `page:agency:${link.key}`,
        kind: "page" as const,
        category: "navigate" as const,
        title: link.label,
        subtitle: link.group === "admin" ? "Admin" : "Agency",
        href: link.path,
        iconKey: link.iconKey,
        matches: hit.matches,
        score: hit.score,
      }];
    })
    .sort(compareResults);
}

export interface CompanyEntry {
  id: string;
  name: string;
  status: string;
}

/**
 * Companies the user can reach. `displayName` applies demo masking, and is what
 * we match against so typing the name on screen finds the row.
 */
export function searchCompanies(
  query: string,
  companies: CompanyEntry[],
  displayName: (name: string) => string,
  queryParams = "",
  currentClientId?: string | null,
): SearchResult[] {
  return companies
    .flatMap((company) => {
      const shown = displayName(company.name);
      const hit = matchLabel(shown, query);
      if (!hit) return [];
      // Bare company route: the server resolves the landing page per viewer.
      const base = `/clients/${company.id}`;
      return [{
        id: `company:${company.id}`,
        kind: "company" as const,
        category: "navigate" as const,
        clientId: company.id,
        title: shown,
        subtitle: company.id === currentClientId
          ? "Current company"
          : company.status === "inactive"
            ? "Company · inactive"
            : "Company",
        href: queryParams ? `${base}?${queryParams}` : base,
        iconKey: "grid",
        matches: hit.matches,
        // Inactive companies are real but rarely what someone is after.
        score: hit.score - (company.status === "inactive" ? 30 : 0),
      }];
    })
    .sort(compareResults);
}

/** Help centre topics, filtered to the articles the current role may open. */
export function searchDocTopics(
  query: string,
  topics: DocTopic[],
  ctx: SearchContext,
): SearchResult[] {
  return searchDocumentation(query, topics.filter((topic) => canReadDocTopic(topic, ctx.role)))
    .map(({ topic, matches, score }) => ({
        id: `help:${topic.slug}`,
        kind: "help" as const,
        category: "help" as const,
        title: topic.title,
        subtitle: topic.category,
        href: `/docs/${topic.slug}`,
        iconKey: "book",
        matches,
        score,
      }));
}
